import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { reportClientError } from '../utils/clientLogger';
import { VendedorLoadingState, VendedorErrorState, VendedorSubmittedState } from '../components/vendedor/VendedorPageStates';
import { VendedorProfileCard } from '../components/vendedor/VendedorProfileCard';
import { VendedorContactCards } from '../components/vendedor/VendedorContactCards';
import { VendedorServicesGrid } from '../components/vendedor/VendedorServicesGrid';
import { VendedorContactFormSection } from '../components/vendedor/VendedorContactFormSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fetchVendedorByCodigo = async (codigo) => {
  const response = await axios.get(`${API}/vendedores/codigo/${codigo}`);
  return response.data;
};

const VendedorPage = () => {
  const navigate = useNavigate();
  const { codigo } = useParams();
  const [vendedor, setVendedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    interesse: 'casa',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadVendedor = useCallback(async () => {
    try {
      const vendedorData = await fetchVendedorByCodigo(codigo);
      setVendedor(vendedorData);
    } catch (error) {
      reportClientError('Erro ao carregar vendedor:', error);
      setError('Vendedor não encontrado');
    } finally {
      setLoading(false);
    }
  }, [codigo]);

  useEffect(() => {
    loadVendedor();
  }, [loadVendedor]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowContactForm(false);
  }, []);

  const handleSubmissionBack = useCallback(() => {
    setSubmitted(false);
    setShowContactForm(false);
    setFormData({ nome: '', email: '', telefone: '', interesse: 'casa' });
  }, []);

  const storeSellerCode = useCallback(() => {
    localStorage.setItem('vendedor_codigo', codigo);
  }, [codigo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/vendedores/${codigo}/lead`, {
        tipo: formData.interesse,
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        detalhes: { origem: 'pagina_vendedor' },
      });
      setSubmitted(true);
    } catch (err) {
      reportClientError('Erro ao enviar lead do vendedor:', err);
      alert('Erro ao enviar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <VendedorLoadingState />;
  }

  if (error) {
    return <VendedorErrorState />;
  }

  if (submitted) {
    return (
      <VendedorSubmittedState
        vendedorName={vendedor.nome}
        onBack={handleSubmissionBack}
        onGoHome={() => navigate('/')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" data-testid="vendedor-page">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-blue-600 font-bold text-xl" data-testid="vendedor-header-home-link">RH Consulting Holdings</Link>
          <span className="text-sm text-gray-500" data-testid="vendedor-status-badge">Consultor Verificado ✓</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <VendedorProfileCard vendedor={vendedor} />
        <VendedorContactCards vendedor={vendedor} />
        <VendedorServicesGrid onStoreSellerCode={storeSellerCode} />
        <VendedorContactFormSection
          vendedorName={vendedor.nome}
          showContactForm={showContactForm}
          formData={formData}
          submitting={submitting}
          onOpenForm={() => setShowContactForm(true)}
          onCloseForm={handleCloseForm}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
        />

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm" data-testid="vendedor-footer-info">
          <p>RH Consulting Holdings - Desde 2007</p>
          <p>1608-7 Yattajima, Isesaki, Gunma, Japan</p>
        </div>
      </div>
    </div>
  );
};

export default VendedorPage;
